import type { ReactNode } from "react";
import { BuildingIcon } from "@/shared/ui/components/basic/icons";
import { ListingTable, type ListingColumn } from "@/shared/ui/components/basic/listing-table";
import { MobileCardView } from "../components/MobileCardView";
import { PropertyGridView } from "../components/PropertyGridView";
import type { PropertyLayout } from "./use-property-layout";
import { EmptyState } from "@/shared/ui/components/basic/EmptyState";
import { DataState, DataStateError, TableRowsSkeleton } from "@/shared/ui";
import type { PropertyResponseType } from "@services/housien/types";
import { useAnimatedHeight } from "./use-animated-height";
import {
  GroupedListingTable,
  GroupSummaryGrid,
  GroupedMobileList,
  GroupedListCountLine,
} from "../components/grouped-listing";
import type { GroupAggregate, GroupBy, PropertyGroup } from "./property-grouping";
import type { GroupView } from "./use-property-grouping";

type Flags = { isLoading: boolean; isError: boolean; isEmpty: boolean };

/** The grouping view model threaded from the page when a dimension is active. */
export interface PropertiesGroupingProps {
  by: Exclude<GroupBy, 'none'>;
  view: GroupView;
  groups: PropertyGroup[];
  aggregates: Map<string, GroupAggregate | null>;
  total: number;
  isCollapsed: (key: string) => boolean;
  onToggleCollapsed: (key: string) => void;
  isShowingAll: (key: string) => boolean;
  onToggleShowAll: (key: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onFilterGroup: (group: PropertyGroup) => void;
}

const SK = 'animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]';

/**
 * Loading placeholder for the properties list header.
 */
function PageHeaderSkeleton() {
  return (
    <div
      className="shrink-0"
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={`${SK} h-7 w-40`} />
        <div className={`${SK} h-9 w-32`} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <div className={`${SK} h-10 flex-1`} />
        <div className={`${SK} h-10 w-24`} />
      </div>
    </div>
  );
}

/**
 * Renders the property list body with loading, error, empty, and table states.
 * @returns The property list body element.
 */
export function PropertiesListBody(props: {
  t: (key: string, options?: Record<string, unknown>) => string;
  isMobile: boolean;
  headerBar: ReactNode;
  dataStateFlags: Flags;
  /** Fetching over a list that is already on screen (re-filter, re-sort). */
  isRefreshing?: boolean;
  effectiveProperties: PropertyResponseType[];
  properties: PropertyResponseType[];
  isLoadingMore: boolean;
  hasReachedEnd?: boolean;
  totalCount?: number | null;
  hasActiveFilters: boolean;
  columns: ListingColumn<PropertyResponseType>[];
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  scrollContainerClassName: string;
  onRefresh: () => void;
  onNewProperty: () => void;
  onRowNavigate: (prop: PropertyResponseType) => void;
  /** Controlled server-sort: the header affordance and click handler. */
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc' | null;
  onSort?: (col: ListingColumn<PropertyResponseType>) => void;
  /** How the flat list draws its rows; ignored while grouped. */
  layout?: PropertyLayout;
  /** When set, the body renders grouped (table or summary) instead of flat. */
  grouping?: PropertiesGroupingProps;
}) {
  const {
    t, isMobile, headerBar, dataStateFlags, isRefreshing, effectiveProperties, properties,
    isLoadingMore, hasReachedEnd, totalCount, hasActiveFilters, columns, scrollContainerRef, sentinelRef,
    scrollContainerClassName, onRefresh, onNewProperty, onRowNavigate, sortKey, sortDir, onSort, layout, grouping,
  } = props;

  // The row count is what moves the bottom edge, so it is what the height
  // animation keys on.
  const desktopListRef = useAnimatedHeight<HTMLDivElement>(properties.length);
  const mobileListRef = useAnimatedHeight<HTMLDivElement>(properties.length);

  const loadingMoreBar = (extra?: string) => (
    <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)', ...(extra ? { background: 'var(--surface-primary)', borderTop: '1px solid var(--border-subtle)' } : {}) }}>
      {t('list.loadingMore')}
    </div>
  );

  const endOfListBar = (extra?: string) => (
    <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)', ...(extra ? { background: 'var(--surface-primary)', borderTop: '1px solid var(--border-subtle)' } : {}) }}>
      {t('list.endOfList')}
    </div>
  );

  return (
    // While a refetch is in flight the rows stay put and only soften, so a
    // filter reads as the list narrowing rather than the page reloading.
    // `aria-busy` says the same thing to a screen reader, which cannot see
    // the fade.
    <div
      className="flex-1 min-h-0 flex flex-col"
      aria-busy={isRefreshing || undefined}
      style={{
        opacity: isRefreshing ? 0.55 : 1,
        pointerEvents: isRefreshing ? 'none' : undefined,
        transition: 'opacity var(--duration-fast, 150ms) ease-out',
      }}
    >
      <DataState
        isLoading={dataStateFlags.isLoading}
        isError={dataStateFlags.isError}
        isEmpty={false}
        onRetry={onRefresh}
        errorMessage={t('list.loadError.message')}
        errorState={
          <DataStateError
            icon={<BuildingIcon width={28} height={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />}
            message={t('list.loadError.message')}
            retryLabel={t('list.loadError.retry')}
            onRetry={onRefresh}
          />
        }
        loadingFallback={<><div className="shrink-0 pb-(--space-4)"><PageHeaderSkeleton /></div><TableRowsSkeleton rows={6} /></>}
      >
        {effectiveProperties.length === 0 ? (
          <>
            {isMobile && headerBar}
            <EmptyState
              icon={<BuildingIcon width={28} height={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />}
              title={hasActiveFilters ? t('list.emptyFiltered.title') || 'Nenhum imóvel encontrado' : t('list.emptyPortfolio.title') || 'Nenhum imóvel cadastrado'}
              description={hasActiveFilters ? t('list.emptyFiltered.description') || 'Tente ajustar os filtros' : t('list.emptyPortfolio.description') || 'Cadastre seu primeiro imóvel'}
              action={hasActiveFilters ? undefined : { label: t('list.newProperty'), onClick: onNewProperty }}
            />
          </>
        ) : (
          <div ref={scrollContainerRef} className={scrollContainerClassName}>
            {isMobile && headerBar}
            {grouping ? (
              <>
                {!isMobile && (
                  <GroupedListCountLine
                    by={grouping.by}
                    totalCount={totalCount ?? grouping.total}
                    groupCount={grouping.groups.length}
                    onExpandAll={grouping.onExpandAll}
                    onCollapseAll={grouping.onCollapseAll}
                  />
                )}
                <div ref={desktopListRef} className="hidden lg:block animate-content-height">
                  {grouping.view === 'summary' ? (
                    <GroupSummaryGrid
                      by={grouping.by}
                      groups={grouping.groups}
                      aggregates={grouping.aggregates}
                      total={grouping.total}
                      onSelectGroup={grouping.onFilterGroup}
                    />
                  ) : (
                    <GroupedListingTable
                      by={grouping.by}
                      groups={grouping.groups}
                      columns={columns}
                      onRowClick={onRowNavigate}
                      onFilterGroup={grouping.onFilterGroup}
                      aggregates={grouping.aggregates}
                      isCollapsed={grouping.isCollapsed}
                      onToggleCollapsed={grouping.onToggleCollapsed}
                      isShowingAll={grouping.isShowingAll}
                      onToggleShowAll={grouping.onToggleShowAll}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      minWidth="860px"
                    />
                  )}
                  {isLoadingMore && loadingMoreBar('surface')}
                  {!isLoadingMore && hasReachedEnd && endOfListBar('surface')}
                </div>
                <div ref={mobileListRef} className="block lg:hidden animate-content-height">
                  <GroupedMobileList
                    groups={grouping.groups}
                    aggregates={grouping.aggregates}
                    onCardClick={onRowNavigate}
                    isCollapsed={grouping.isCollapsed}
                    onToggleCollapsed={grouping.onToggleCollapsed}
                    t={t}
                  />
                  {isLoadingMore && loadingMoreBar()}
                  {!isLoadingMore && hasReachedEnd && endOfListBar()}
                </div>
              </>
            ) : (
              <>
                {totalCount != null && (
                  <div style={{ padding: 'var(--space-1) var(--space-1) var(--space-2)', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}>
                    {t('list.resultsCount', { count: totalCount })}
                  </div>
                )}
                {layout === 'grid' ? (
                  // The grid reflows on its own, so it covers the tablet
                  // widths the table hands to the mobile cards.
                  <div ref={desktopListRef} className="animate-content-height">
                    <PropertyGridView properties={properties} onCardClick={onRowNavigate} t={t} />
                    {isLoadingMore && loadingMoreBar()}
                    {!isLoadingMore && hasReachedEnd && endOfListBar()}
                  </div>
                ) : (
                  <>
                    <div ref={desktopListRef} className="hidden lg:block animate-content-height">
                      <ListingTable
                        data={properties}
                        columns={columns}
                        onRowClick={onRowNavigate}
                        density="compact"
                        minWidth="860px"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={onSort}
                      />
                      {isLoadingMore && loadingMoreBar('surface')}
                      {!isLoadingMore && hasReachedEnd && endOfListBar('surface')}
                    </div>
                    <div ref={mobileListRef} className="block lg:hidden animate-content-height">
                      <MobileCardView properties={properties} onCardClick={onRowNavigate} t={t} />
                      {isLoadingMore && loadingMoreBar()}
                      {!isLoadingMore && hasReachedEnd && endOfListBar()}
                    </div>
                  </>
                )}
              </>
            )}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </div>
        )}
      </DataState>
    </div>
  );
}
